import { useState } from "react";
import { z } from "zod";
import {
  Box,
  Textarea,
  Checkbox,
  Text,
  Button,
  ButtonGroup,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
} from "@chakra-ui/react";
import { addPatientsToCohort, removePatientsFromCohort } from "../../../api/internal";
import { Cohort } from "../../../api/schemas/cohort";
import { Label } from "../../shared/Label";
import useMetriportToast from "../../shared/toast";
import { captureAndDisplayError } from "../../shared/util";

const uuidSchema = z.string().uuid();

function validatePatientIds(input: string): { valid: string[]; invalid: string[] } {
  const lines = input
    .split("\n")
    .map(line => line.trim())
    .filter(Boolean);
  const valid: string[] = [];
  const invalid: string[] = [];
  for (const line of lines) {
    if (uuidSchema.safeParse(line).success) {
      valid.push(line);
    } else {
      invalid.push(line);
    }
  }
  return { valid, invalid };
}

export default function CohortPatientsForm({
  isOpen,
  onClose,
  onSubmit,
  cxId,
  cohortId,
  cohortName,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (cohort: Cohort) => void;
  cxId: string;
  cohortId: string;
  cohortName: string;
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [mode, setMode] = useState<"add" | "remove">("add");
  const [patientIds, setPatientIds] = useState("");
  const [allPatients, setAllPatients] = useState(false);
  const [validationError, setValidationError] = useState<string>();
  const toast = useMetriportToast();

  const isAdd = mode === "add";

  function handleClose() {
    setPatientIds("");
    setAllPatients(false);
    setValidationError(undefined);
    setMode("add");
    onClose();
  }

  async function handleSubmit() {
    setValidationError(undefined);
    setIsSubmitting(true);

    try {
      let body: string;

      if (allPatients) {
        body = JSON.stringify({ allPatients: true });
      } else {
        const { valid, invalid } = validatePatientIds(patientIds);

        if (invalid.length > 0) {
          setValidationError(`Invalid UUIDs: ${invalid.join(", ")}`);
          setIsSubmitting(false);
          return;
        }

        if (valid.length === 0) {
          setValidationError("Please enter at least one patient ID");
          setIsSubmitting(false);
          return;
        }

        body = JSON.stringify({ patientIds: valid });
      }

      const updatedCohort = isAdd
        ? await addPatientsToCohort({ cxId, cohortId, body })
        : await removePatientsFromCohort({ cxId, cohortId, body });

      toast.success({ title: `Patients ${isAdd ? "added to" : "removed from"} ${cohortName}` });
      onSubmit(updatedCohort);
      handleClose();
    } catch (error) {
      const axiosError = error as {
        response?: { data?: { detail?: string } };
        message?: string;
      };
      const errorMsg =
        axiosError?.response?.data?.detail ??
        axiosError?.message ??
        `Failed to ${isAdd ? "add" : "remove"} patients`;
      toast.error({ title: errorMsg, duration: 10000 });
      captureAndDisplayError({
        error,
        msg: errorMsg,
        context: isAdd ? "addPatientsToCohort" : "removePatientsFromCohort",
        captureMsg: `${isAdd ? "Add" : "Remove"} patients failed`,
        toast,
        hideDisplay: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Drawer isOpen={isOpen} placement="right" onClose={handleClose} size="lg">
      <DrawerOverlay />
      <DrawerContent motionProps={{ initial: "none", exit: "none" }}>
        <DrawerCloseButton />
        <DrawerHeader>Update Patients in {cohortName}</DrawerHeader>
        <DrawerBody>
          <Box>
            <Label>Action</Label>
            <ButtonGroup size="sm" isAttached mt={1} isDisabled={allPatients}>
              <Button
                colorScheme={isAdd ? "blue" : "gray"}
                variant={isAdd ? "solid" : "outline"}
                onClick={() => setMode("add")}
              >
                Add
              </Button>
              <Button
                colorScheme={!isAdd ? "red" : "gray"}
                variant={!isAdd ? "solid" : "outline"}
                onClick={() => setMode("remove")}
              >
                Remove
              </Button>
            </ButtonGroup>

            <Box mt={4}>
              <Checkbox isChecked={allPatients} onChange={e => setAllPatients(e.target.checked)}>
                {isAdd ? "Add all patients" : "Remove all patients"}
              </Checkbox>
            </Box>

            <Box mt={4}>
              <Label>Patient IDs (one per line)</Label>
              <Textarea
                value={patientIds}
                onChange={e => setPatientIds(e.target.value)}
                fontFamily="mono"
                fontSize="sm"
                rows={10}
                placeholder={
                  "00000000-0000-0000-0000-000000000000\n11111111-1111-1111-1111-111111111111"
                }
                isDisabled={allPatients}
                opacity={allPatients ? 0.5 : 1}
              />
            </Box>

            {validationError && (
              <Text mt={2} color="red.500" fontSize="sm">
                {validationError}
              </Text>
            )}
          </Box>
        </DrawerBody>
        <DrawerFooter borderTopWidth="1px">
          <Button
            colorScheme={isAdd ? "blue" : "red"}
            mr={3}
            isLoading={isSubmitting}
            onClick={handleSubmit}
          >
            {isAdd ? "Add Patients" : "Remove Patients"}
          </Button>
          <Button variant="outline" onClick={handleClose}>
            Close
          </Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
}
